import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Receipt, 
  Download, 
  Clock,
  CheckCircle,
  XCircle,
  Loader2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency, type Currency } from "@/lib/currency";
import { fetchSupabaseFunction } from "@/lib/shopifySessionToken";
import { format } from "date-fns";
import { toast } from "sonner";

interface Payment {
  charge_origin: "subscription_payment" | "shopify_usage_charge";
  billing_provider?: string | null;
  charge_type?: string | null;
  id: string;
  invoice_number: string;
  plan: string;
  billing_cycle: string;
  amount: number;
  currency: string;
  status: string;
  paid_at: string | null;
  created_at: string;
  razorpay_payment_id: string | null;
  external_charge_id?: string | null;
  description?: string | null;
  shopify_app_subscription_id?: string | null;
}

interface ShopifyUsageCharge {
  amount: number;
  created_at: string;
  currency: string;
  description: string;
  id: string;
  request_id: string;
  shopify_usage_record_id: string | null;
  status: string;
}

interface Props {
  merchantId: string;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function PaymentHistory({ merchantId }: Props) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    fetchPayments();
  }, [merchantId]);

  const fetchPayments = async () => {
    try {
      const [{ data: subscriptionPayments, error: subscriptionError }, { data: usageCharges, error: usageError }] = await Promise.all([
        supabase
          .from("subscription_payments")
          .select("*")
          .eq("merchant_id", merchantId)
          .order("created_at", { ascending: false }),
        supabase
          .from("shopify_usage_charges")
          .select("id, amount, currency, description, created_at, request_id, shopify_usage_record_id, status")
          .eq("merchant_id", merchantId)
          .order("created_at", { ascending: false }),
      ]);

      if (subscriptionError) throw subscriptionError;
      if (usageError) throw usageError;

      const normalizedPayments: Payment[] = (subscriptionPayments || []).map((payment) => ({
        ...payment,
        amount: Number(payment.amount) || 0,
        charge_origin: "subscription_payment",
      }));

      const normalizedUsageCharges: Payment[] = ((usageCharges || []) as ShopifyUsageCharge[]).map((charge) => ({
        amount: Number(charge.amount) || 0,
        billing_cycle: "monthly",
        billing_provider: "shopify",
        charge_origin: "shopify_usage_charge",
        charge_type: "usage",
        created_at: charge.created_at,
        currency: charge.currency || "USD",
        description: charge.description,
        external_charge_id: charge.shopify_usage_record_id,
        id: `usage-${charge.id}`,
        invoice_number: `USAGE-${charge.request_id.slice(0, 8).toUpperCase()}`,
        paid_at: charge.status === "paid" ? charge.created_at : null,
        plan: "usage",
        razorpay_payment_id: null,
        shopify_app_subscription_id: null,
        status: charge.status,
      }));

      const mergedPayments = [...normalizedPayments, ...normalizedUsageCharges].sort((left, right) =>
        new Date(right.created_at).getTime() - new Date(left.created_at).getTime(),
      );

      setPayments(mergedPayments);
    } catch (error) {
      console.error("Error fetching payments:", error);
      toast.error("Failed to load payment history");
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async (payment: Payment) => {
    if (payment.status !== "paid") {
      toast.error("Invoice only available for completed payments");
      return;
    }

    setDownloadingId(payment.id);
    try {
      const response = await fetchSupabaseFunction("generate-subscription-invoice", {
        body: { paymentId: payment.id },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to generate invoice");
      }

      const html = await response.text();
      
      // Open in new window for printing/saving
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
      } else {
        toast.error("Please allow popups to download invoice");
      }
    } catch (error: any) {
      console.error("Error downloading invoice:", error);
      toast.error(error.message || "Failed to download invoice");
    } finally {
      setDownloadingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return (
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
            <CheckCircle className="h-3 w-3 mr-1" />
            Paid
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-red-500/10 text-red-600 border-red-500/20">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPlanLabel = (plan: string) => {
    const labels: Record<string, string> = {
      trial: "Trial",
      free: "Free",
      starter: "Starter",
      growth: "Growth",
      enterprise: "Enterprise",
    };
    return labels[plan] || plan;
  };

  const getPaymentLabel = (payment: Payment) => {
    if (payment.charge_type === "wallet_topup") {
      return "Wallet Top-up";
    }

    if (payment.charge_type === "usage") {
      return payment.charge_origin === "shopify_usage_charge"
        ? payment.description || "Shopify usage charge"
        : payment.description || "Commission charge";
    }

    return `${getPlanLabel(payment.plan)} (${payment.billing_cycle === "annual" ? "Annual" : "Monthly"})`;
  };

  const canDownloadInvoice = (payment: Payment) =>
    payment.status === "paid" &&
    payment.charge_origin === "subscription_payment" &&
    payment.charge_type !== "usage";

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Billing History
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-8 w-20" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          Billing History
        </CardTitle>
        <CardDescription>
          View plan payments, Shopify usage charges, wallet top-ups, and download invoices
        </CardDescription>
      </CardHeader>
      <CardContent>
        {payments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No billing history yet</p>
            <p className="text-sm">Your payments and wallet top-ups will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {payments.map((payment) => (
              <div
                key={payment.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${
                    payment.status === "paid" 
                      ? "bg-green-500/10" 
                      : payment.status === "pending"
                      ? "bg-yellow-500/10"
                      : "bg-red-500/10"
                  }`}>
                    <Receipt className={`h-4 w-4 ${
                      payment.status === "paid"
                        ? "text-green-600"
                        : payment.status === "pending"
                        ? "text-yellow-600"
                        : "text-red-600"
                    }`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium">
                        {payment.invoice_number}
                      </span>
                      {getStatusBadge(payment.status)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {getPaymentLabel(payment)}
                      {" • "}
                      {payment.paid_at 
                        ? format(new Date(payment.paid_at), "MMM d, yyyy")
                        : format(new Date(payment.created_at), "MMM d, yyyy")}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-semibold">{formatCurrency(payment.amount, (payment.currency as Currency) || "USD")}</p>
                    {payment.razorpay_payment_id && (
                      <p className="text-xs text-muted-foreground font-mono">
                        {payment.razorpay_payment_id.slice(0, 12)}...
                      </p>
                    )}
                    {!payment.razorpay_payment_id && payment.external_charge_id && (
                      <p className="text-xs text-muted-foreground font-mono">
                        {payment.external_charge_id.slice(0, 18)}...
                      </p>
                    )}
                  </div>
                  {canDownloadInvoice(payment) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadInvoice(payment)}
                      disabled={downloadingId === payment.id}
                    >
                      {downloadingId === payment.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-1" />
                          Invoice
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
